import React from 'react';
import { Button } from '../../ui/Button';

export interface ArchiveDetailRow {
  label: string;
  value: string;
}

interface SessionsArchiveDialogProps {
  isOpen: boolean;
  /** Primary display name for the session */
  sessionName: string;
  /** Extra context (date, team, boat, …) */
  detailRows?: ArchiveDetailRow[];
  isArchiving: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const SessionsArchiveDialog: React.FC<SessionsArchiveDialogProps> = ({
  isOpen,
  sessionName,
  detailRows = [],
  isArchiving,
  error,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-lg"
        data-testid="archive-confirmation-dialog"
      >
        <h3 className="text-lg font-medium text-gray-900 mb-1">Archive session</h3>
        <p className="text-xs text-gray-500 mb-4">
          Confirm this is the session you intend to archive. You can restore it later from Archived
          sessions.
        </p>

        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 mb-4">
          <p className="text-sm font-semibold text-gray-900 break-words">{sessionName}</p>
          {detailRows.length > 0 && (
            <dl className="mt-3 space-y-2 text-sm border-t border-gray-200 pt-3">
              {detailRows.map((row, idx) => (
                <div
                  key={`${idx}-${row.label}`}
                  className="grid grid-cols-[7rem_1fr] gap-2 items-start"
                >
                  <dt className="text-gray-500 shrink-0 text-xs">{row.label}</dt>
                  <dd
                    className="text-gray-900 text-right break-words text-xs leading-snug"
                    title={row.value}
                  >
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Archived sessions are hidden from your active session lists and default dashboards until
          you unarchive them.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={onCancel} disabled={isArchiving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={isArchiving}>
            {isArchiving ? 'Archiving...' : 'Archive'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SessionsArchiveDialog;
